import { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Loader2, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../redux/store";
import { RequestorDetails } from "../../../components/notificationv2";
import { callPhpWorkflow } from "../../../redux/features/common";
import { WorkflowRequest } from "../../../types";
import { DateTimeUtil } from "../../../utils";
import { NgwAvatar } from "../../../components/common";
import { ApprovalHistory } from "../../../components/notificationv2";

const SwapNotificationDetail: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { isNotificationDetailLoading, notificationDetail } = useSelector((state: RootState) => state.notification);
    const user = useSelector((state: RootState) => state.user.user);
    const { isCallingWorkflow } = useSelector((state: RootState) => state.common);

    const [selectedActors, setSelectedActors] = useState<{ [key: string]: string[] }>({});
    const [unselectedActors, setUnselectedActors] = useState<{ [key: string]: string[] }>({});
    const [notes, setNotes] = useState<string>("");

    const requestor = notificationDetail?.shift_swapping_details?.requestor;
    const requestorDetails = notificationDetail?.shift_swapping_details?.requestor_details;
    const selectionType = notificationDetail?.workflow_current_step_details?.selection_type || "single";
    const currentStepStatus = notificationDetail?.workflow_current_step_details?.current_status || "0";
    const actionConsecutiveDays = Number(notificationDetail?.shift_swapping_details?.days_in_swapping_block || "1");
    const getEffectiveActionConsecutiveDays = actionConsecutiveDays > 1 ? actionConsecutiveDays - 1 : undefined;
    const exception = notificationDetail?.shift_swapping_details?.exception;
    const hasException = exception && exception.error_status && exception.error_status !== null;
    const isActionsDisabled = hasException || isCallingWorkflow;
    
    const getFilteredWorkflowDetails = useCallback(() => {
        const details = notificationDetail?.workflow_details?.flatMap((workflow) => {
            const actorMap = workflow.actor_json;
            const matchingActorEntry = Object.entries(actorMap || {}).find(
                ([employeeId]) => employeeId == user.os_emp_account_id?.toString()
            );

            console.log("workflow", workflow);
            console.log("notificationDetail?.workflow_current_step_details?.step_code", notificationDetail?.workflow_current_step_details?.step_code);
            console.log("matchingActorEntry", matchingActorEntry);

            if (
                workflow.step_code === notificationDetail?.workflow_current_step_details?.step_code &&
                matchingActorEntry
            ) {
                const [employeeId, actor] = matchingActorEntry;
                return [
                    {
                        ...workflow,
                        actor_json: { [employeeId]: actor },
                    },
                ];
            }

            return [];
        }) || [];
        return details;
    }, [notificationDetail, user?.os_emp_account_id]);

    const filteredWorkflowDetails = useMemo(() => getFilteredWorkflowDetails(), [getFilteredWorkflowDetails]);

    useEffect(() => {
        const allActors: { [key: string]: string[] } = {};

        filteredWorkflowDetails.forEach((workflow: any) => {
            const actorEntry = Object.entries(workflow.actor_json || {})[0]; // we already narrowed it to one in previous step
            if (actorEntry) {
                const [employeeId, actor] = actorEntry;
                const scheduleId = (actor as any).schedule_details?.[0]?.schedule_id ?? "";
                if (employeeId && scheduleId && !selectedActors[employeeId]) {
                    if (!allActors[employeeId]) allActors[employeeId] = [];
                    allActors[employeeId].push(scheduleId);
                }
            }
        });

        setUnselectedActors((prev) => {
            if (JSON.stringify(prev) !== JSON.stringify(allActors)) {
                return allActors;
            }
            return prev;
        });
    }, [filteredWorkflowDetails]);

    /**
     * Handles Approve action
     */
    const handleApprove = async () => {
        if (hasException) {
            return; // Don't proceed if there's an exception
        }
        if (!selectedActors || Object.keys(selectedActors).length === 0) {
            return; // Don't proceed if no actors selected
        }
        const request: WorkflowRequest = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 1,
            actor_id: user?.id?.toString(),
            user_account_id: user?.userAccountId?.toString(),
            reference_id: notificationDetail?.alert_json?.REF_ID?.toString(),
            result: { APPROVED: selectedActors, REJECTED: {} },
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            notes,
        };

        await dispatch(callPhpWorkflow(request));
        navigate("/notifications");
    };

    /**
     * Handles Reject action
     */
    const handleReject = async () => {
        if (hasException) {
            return; // Don't proceed if there's an exception
        }
        if (!selectedActors || Object.keys(selectedActors).length === 0) {
            return; // Don't proceed if no actors selected
        }
        const request: WorkflowRequest = {
            workflow_code: notificationDetail?.alert_json?.WF_CODE,
            status: 0,
            actor_id: user?.id?.toString(),
            user_account_id: user?.userAccountId?.toString(),
            reference_id: notificationDetail?.alert_json?.REF_ID?.toString(),
            result: { REJECTED: selectedActors, APPROVED: {} },
            wf_txn_lines_id: notificationDetail?.alert_json?.WF_TXN_LINES_ID,
            workflow_header_id: notificationDetail?.alert_json?.WF_HEADER_ID,
            notes,
        };

        await dispatch(callPhpWorkflow(request));
        navigate("/notifications");
    };

    const handleCheckboxChange = (employeeId: string, scheduleId: string) => {
        setSelectedActors((prev) => {
            if (selectionType === "single") {
                // ✅ Reset all selections for this employee (Single Selection Mode)
                return { [employeeId]: [scheduleId] };
            } else {
                // ✅ Multiple selection mode (Toggle logic)
                const currentSelection = prev[employeeId] || [];
                const isSelected = currentSelection.includes(scheduleId);

                return {
                    ...prev,
                    [employeeId]: isSelected
                        ? currentSelection.filter((id) => id !== scheduleId) // Remove if already selected
                        : [...currentSelection, scheduleId], // Add if not selected
                };
            }
        });
    };

    if (isNotificationDetailLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2
                    className="w-8 h-8 text-g
                ray-600 animate-spin"
                />
            </div>
        );
    }

    // if (!notificationDetail && !isNotificationDetailLoading) {
    //     return (
    //         <div className="text-center text-gray-500 flex flex-col items-center gap-4 w-full">
    //             <p>No notification details available.</p>
    //             <button
    //                 onClick={() => navigate(-1)}
    //                 className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
    //             >
    //                 <ChevronLeft className="w-5 h-5" />
    //                 <span>Back</span>
    //             </button>
    //         </div>
    //     );
    // }

    // Approval History (unified view similar to timeoff)
    const CurrentApproversSection = () => {
        return <ApprovalHistory workflowDetails={notificationDetail?.workflow_details} />;
    };

    return (
        <div className="w-full px-6 bg-white h-full overflow-y-auto mx-auto md:w-1/2">
            {/* Message Info */}
            {notificationDetail?.message_info && (
                <div className="flex flex-col border-b pb-2">
                    <h2 className="text-xl font-semibold text-gray-800">{notificationDetail?.message_info.title}</h2>
                    {/* <p className="text-gray-600 mt-1">{notificationDetail?.message_info.body}</p> */}
                </div>
            )}

            {/* Requestor Details */}
            <RequestorDetails
                requestor={requestor}
                requestorDetails={requestorDetails}
                effectiveConsecutiveDays={getEffectiveActionConsecutiveDays}
            />

            {/* Warning Message for Exceptions */}
            {hasException && (
                <div className="p-4 mt-6 rounded-lg bg-red-50 border border-red-200">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                            <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-red-800">
                                {exception.error_status?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                            </h3>
                            <p className="text-sm text-red-700 mt-1">
                                {exception.error_message}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval history */}
            <CurrentApproversSection />

            {/* Only show action section if action is required */}
            {notificationDetail?.only_notify_no_action !== "1" && currentStepStatus === "0" && (
                <div className="p-4 mt-6">
                    <h3 className="text-lg font-semibold text-gray-700">Approve Shifts</h3>
                    <p className="text-gray-600 text-sm mb-4">
                        {selectionType === "single"
                            ? "Select one actor & schedule"
                            : "Select multiple actors & schedules"}
                    </p>

                    {/* Workflow Actors & Schedule Options */}
                    <div className="space-y-4">
                        {filteredWorkflowDetails.map((workflow: any, index: number) => (
                            <div key={index} className="p-4 shadow-sm space-y-4">
                                {workflow.wf_event_json?.SWAPPING_DETAILS?.Approved?.map((approved: any, idx: number) => (
                                    <div key={idx} className="space-y-2">
                                        {/* Actor Info */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <NgwAvatar src={approved.image_url} name={approved.name} />
                                            <p className="text-sm font-medium text-gray-800">{approved.name}</p>
                                        </div>

                                        {/* Schedule List */}
                                        <div className="space-y-2">
                                            {approved.schedule_details?.map((schedule: any, sIdx: number) => (
                                                <label
                                                    key={sIdx}
                                                    className="flex items-start gap-2 bg-white border rounded-md px-3 py-2 hover:shadow-sm transition"
                                                >
                                                    <input
                                                        type={selectionType === "single" ? "radio" : "checkbox"}
                                                        name={
                                                            selectionType === "single"
                                                                ? approved.employee_id || ""
                                                                : undefined
                                                        }
                                                        checked={selectedActors[approved.employee_id || ""]?.includes(
                                                            schedule.schedule_id || ""
                                                        )}
                                                        onChange={() =>
                                                            handleCheckboxChange(
                                                                approved.employee_id!,
                                                                schedule.schedule_id!
                                                            )
                                                        }
                                                        className="mt-1 w-4 h-4 accent-gray-700"
                                                    />
                                                    <div className="text-sm text-gray-700">
                                                        <p className="font-semibold">{schedule.shift_name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {DateTimeUtil.DisplayDateTime(schedule.schedule_start_date!)}{" "}
                                                            to{" "}
                                                            {DateTimeUtil.DisplayDateTime(schedule.schedule_end_date!)}
                                                            {getEffectiveActionConsecutiveDays !== undefined &&
                                                                getEffectiveActionConsecutiveDays > 1 && (
                                                                    <span>
                                                                        {" "}
                                                                        + {getEffectiveActionConsecutiveDays} days
                                                                    </span>
                                                                )}
                                                        </p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Notes Section */}
                    <textarea
                        className="w-full p-3 border border-gray-300 rounded-md mt-6 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                        placeholder="Add notes (optional)..."
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />

                    {/* Approve & Reject Buttons */}
                    <div className="flex justify-end mt-6 gap-4">
                        {isCallingWorkflow ? (
                            <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                        ) : (
                            <>
                                <button
                                    onClick={handleApprove}
                                    disabled={isActionsDisabled}
                                    className={`px-4 py-2 rounded-lg transition ${
                                        isActionsDisabled
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                            : 'bg-green-500 text-white hover:bg-green-700'
                                    }`}
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={handleReject}
                                    disabled={isActionsDisabled}
                                    className={`px-4 py-2 rounded-lg transition ${
                                        isActionsDisabled
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                            : 'bg-red-500 text-white hover:bg-red-700'
                                    }`}
                                >
                                    Reject
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Show FYI message when it's read-only */}
            {notificationDetail?.only_notify_no_action === "1" && (
                <div className="p-4 mt-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-800">
                                <strong>For Your Information:</strong> This notification is for informational purposes only. No action is required from you.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SwapNotificationDetail;
